import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { useUserContext } from "@/hooks/useUserContext";
import { toast } from "sonner";
import { ShieldCheck, AlertTriangle, CheckCircle2, XCircle, FileCheck2, Globe2, BookCheck, Plus, Trash2, FileDown, Sparkles, Loader2, Inbox, ClipboardList } from "lucide-react";
import AuditBinderDialog, { AuditBinder } from "@/components/admin/AuditBinderDialog";
import ComplianceTreeView from "@/components/admin/ComplianceTreeView";
import AutoLinkReviewQueue from "@/components/admin/AutoLinkReviewQueue";
import AssessmentsTab from "@/components/admin/AssessmentsTab";

interface Framework {
  id: string;
  code: string;
  name: string;
  name_id: string | null;
  version: string | null;
  jurisdiction: string | null;
  description: string | null;
  is_system: boolean;
}

interface Control {
  control_id: string;
  clause_code: string;
  title: string;
  criticality: string;
  evidence_count: number;
  valid_evidence_count: number;
  expiring_evidence_count: number;
  status: "compliant" | "partial" | "at_risk" | "gap";
}

interface ScopeRow {
  id: string;
  framework_id: string;
  framework_code: string;
  framework_name: string;
  status: string;
  target_audit_date: string | null;
}

const STATUS_META: Record<string, { label: string; className: string; icon: any }> = {
  compliant: { label: "Compliant", className: "bg-success/10 text-success border-success/20", icon: CheckCircle2 },
  partial:   { label: "Partial",   className: "bg-warning/10 text-warning border-warning/20", icon: AlertTriangle },
  at_risk:   { label: "At Risk",   className: "bg-destructive/10 text-destructive border-destructive/20", icon: AlertTriangle },
  gap:       { label: "Gap",       className: "bg-muted text-muted-foreground border-border", icon: XCircle },
};

const CRITICALITY_META: Record<string, string> = {
  critical: "bg-destructive/10 text-destructive border-destructive/20",
  major:    "bg-warning/10 text-warning border-warning/20",
  minor:    "bg-muted text-muted-foreground border-border",
  observation: "bg-muted text-muted-foreground border-border",
};

const AdminCompliance = () => {
  const { companyId, isLoading: ctxLoading } = useUserContext();
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [scope, setScope] = useState<ScopeRow[]>([]);
  const [activeFrameworkId, setActiveFrameworkId] = useState<string>("");
  const [controls, setControls] = useState<Control[]>([]);
  const [loading, setLoading] = useState(true);
  const [adoptingId, setAdoptingId] = useState<string>("");

  // Exports state
  const [exportFrameworkId, setExportFrameworkId] = useState<string>("");
  const [periodStart, setPeriodStart] = useState<string>("");
  const [periodEnd, setPeriodEnd] = useState<string>("");
  const [includeNarratives, setIncludeNarratives] = useState<boolean>(true);
  const [generatingBinder, setGeneratingBinder] = useState(false);
  const [binder, setBinder] = useState<AuditBinder | null>(null);
  const [binderOpen, setBinderOpen] = useState(false);
  const [runningSelfAssessment, setRunningSelfAssessment] = useState(false);
  const [selfAssessment, setSelfAssessment] = useState<any | null>(null);

  async function generateBinder() {
    if (!companyId || !exportFrameworkId) {
      toast.error("Pilih framework terlebih dahulu");
      return;
    }
    setGeneratingBinder(true);
    setBinder(null);
    try {
      const { data, error } = await supabase.functions.invoke("compliance-audit-binder", {
        body: {
          framework_id: exportFrameworkId,
          company_id: companyId,
          site_id: null,
          lang: "id",
          include_narratives: includeNarratives,
          period_start: periodStart || null,
          period_end: periodEnd || null,
        },
      });
      if (error) throw error;
      setBinder(data as AuditBinder);
      setBinderOpen(true);
      toast.success("Binder berhasil dibuat");
    } catch (e: any) {
      console.error(e);
      toast.error("Gagal membuat binder: " + (e?.message || "unknown"));
    } finally {
      setGeneratingBinder(false);
    }
  }

  async function runSelfAssessment() {
    if (!companyId || !exportFrameworkId) {
      toast.error("Pilih framework terlebih dahulu");
      return;
    }
    setRunningSelfAssessment(true);
    setSelfAssessment(null);
    try {
      const { data, error } = await supabase.functions.invoke("compliance-self-assessment", {
        body: { framework_id: exportFrameworkId, company_id: companyId, site_id: null, lang: "id" },
      });
      if (error) throw error;
      setSelfAssessment(data);
      toast.success("Self-assessment selesai");
    } catch (e: any) {
      console.error(e);
      toast.error("Gagal: " + (e?.message || "unknown"));
    } finally {
      setRunningSelfAssessment(false);
    }
  }

  // Initial load
  useEffect(() => {
    if (ctxLoading || !companyId) return;
    void loadAll();
  }, [companyId, ctxLoading]);

  // Reload controls when active framework changes
  useEffect(() => {
    if (!activeFrameworkId || !companyId) return;
    void loadControls(activeFrameworkId);
  }, [activeFrameworkId, companyId]);

  async function loadAll() {
    setLoading(true);
    try {
      const [fwRes, scopeRes] = await Promise.all([
        supabase
          .from("compliance_frameworks")
          .select("*")
          .eq("is_active", true)
          .order("code"),
        supabase
          .from("compliance_company_scope")
          .select("id, framework_id, status, target_audit_date, compliance_frameworks(code, name)")
          .eq("company_id", companyId!),
      ]);

      if (fwRes.error) throw fwRes.error;
      if (scopeRes.error) throw scopeRes.error;

      setFrameworks((fwRes.data || []) as Framework[]);

      const scopeRows: ScopeRow[] = ((scopeRes.data || []) as any[]).map((r) => ({
        id: r.id,
        framework_id: r.framework_id,
        framework_code: r.compliance_frameworks?.code ?? "",
        framework_name: r.compliance_frameworks?.name ?? "",
        status: r.status,
        target_audit_date: r.target_audit_date,
      }));
      setScope(scopeRows);

      // Pick first adopted framework, else first system framework
      const firstId = scopeRows[0]?.framework_id || (fwRes.data?.[0] as any)?.id || "";
      if (firstId) setActiveFrameworkId(firstId);
    } catch (e: any) {
      console.error(e);
      toast.error("Failed to load compliance data: " + e.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadControls(frameworkId: string) {
    if (!companyId) return;
    try {
      const { data, error } = await supabase.rpc("get_compliance_status", {
        p_company_id: companyId,
        p_framework_id: frameworkId,
        p_site_id: null,
      });
      if (error) throw error;
      setControls((data || []) as Control[]);
    } catch (e: any) {
      console.error(e);
      toast.error("Failed to load controls: " + e.message);
    }
  }

  async function adoptFramework(frameworkId: string) {
    if (!companyId) return;
    setAdoptingId(frameworkId);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const { error } = await supabase.from("compliance_company_scope").insert({
        company_id: companyId,
        framework_id: frameworkId,
        status: "active",
        adopted_by: userData.user?.id,
      });
      if (error) throw error;
      toast.success("Framework adopted");
      await loadAll();
    } catch (e: any) {
      console.error(e);
      toast.error("Failed to adopt: " + e.message);
    } finally {
      setAdoptingId("");
    }
  }

  async function removeScope(id: string) {
    if (!confirm("Remove this framework from your compliance scope? Evidence links remain but the framework is unscheduled.")) return;
    try {
      const { error } = await supabase.from("compliance_company_scope").delete().eq("id", id);
      if (error) throw error;
      toast.success("Framework removed from scope");
      await loadAll();
    } catch (e: any) {
      toast.error("Failed: " + e.message);
    }
  }

  const adoptedIds = useMemo(() => new Set(scope.map((s) => s.framework_id)), [scope]);
  const availableToAdopt = frameworks.filter((f) => !adoptedIds.has(f.id));
  const activeFramework = frameworks.find((f) => f.id === activeFrameworkId);

  const summary = useMemo(() => {
    const total = controls.length;
    const compliant = controls.filter((c) => c.status === "compliant").length;
    const partial = controls.filter((c) => c.status === "partial").length;
    const atRisk = controls.filter((c) => c.status === "at_risk").length;
    const gap = controls.filter((c) => c.status === "gap").length;
    const score = total > 0 ? Math.round((compliant / total) * 100) : 0;
    return { total, compliant, partial, atRisk, gap, score };
  }, [controls]);

  if (ctxLoading || loading) {
    return <div className="p-6 text-muted-foreground">Loading compliance data…</div>;
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-primary" />
            Compliance & Audit Management
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Continuous, evidence-backed compliance for SMK3, ISO 45001, OSHA, and internal audits.
          </p>
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="controls">Controls</TabsTrigger>
          <TabsTrigger value="assessments"><ClipboardList className="w-3.5 h-3.5 mr-1" />Assessments</TabsTrigger>
          <TabsTrigger value="review"><Inbox className="w-3.5 h-3.5 mr-1" />Auto-Link Review</TabsTrigger>
          <TabsTrigger value="frameworks">Frameworks</TabsTrigger>
          <TabsTrigger value="exports">Exports</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="space-y-4">
          {scope.length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center space-y-3">
                <BookCheck className="w-10 h-10 text-muted-foreground mx-auto" />
                <p className="text-muted-foreground">
                  No frameworks adopted yet. Open the <strong>Frameworks</strong> tab to adopt SMK3, ISO 45001 or OSHA.
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card>
                  <CardContent className="p-4">
                    <div className="text-xs text-muted-foreground">Adopted Frameworks</div>
                    <div className="text-2xl font-bold mt-1">{scope.length}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-xs text-muted-foreground">Compliant Controls</div>
                    <div className="text-2xl font-bold mt-1 text-success">{summary.compliant}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-xs text-muted-foreground">Gaps</div>
                    <div className="text-2xl font-bold mt-1 text-destructive">{summary.gap + summary.atRisk}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-xs text-muted-foreground">Audit Readiness</div>
                    <div className="text-2xl font-bold mt-1 text-primary">{summary.score}%</div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">My Compliance Scope</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Framework</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Target Audit</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {scope.map((s) => (
                        <TableRow key={s.id}>
                          <TableCell>
                            <div className="font-medium">{s.framework_name}</div>
                            <div className="text-xs text-muted-foreground">{s.framework_code}</div>
                          </TableCell>
                          <TableCell><Badge variant="outline">{s.status}</Badge></TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {s.target_audit_date ?? "—"}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" onClick={() => removeScope(s.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* CONTROLS — hierarchical tree */}
        <TabsContent value="controls" className="space-y-4">
          {companyId && activeFrameworkId ? (
            <ComplianceTreeView
              companyId={companyId}
              frameworkId={activeFrameworkId}
              frameworks={frameworks}
              onFrameworkChange={setActiveFrameworkId}
            />
          ) : (
            <Card><CardContent className="py-10 text-center text-muted-foreground">Pilih framework di tab Frameworks terlebih dahulu.</CardContent></Card>
          )}
        </TabsContent>

        {/* ASSESSMENTS — audit matrix */}
        <TabsContent value="assessments" className="space-y-4">
          {companyId && <AssessmentsTab companyId={companyId} frameworks={frameworks} />}
        </TabsContent>

        {/* AUTO-LINK REVIEW QUEUE */}
        <TabsContent value="review" className="space-y-4">
          {companyId && <AutoLinkReviewQueue companyId={companyId} />}
        </TabsContent>

        {/* FRAMEWORKS */}
        <TabsContent value="frameworks" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Available Frameworks</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {frameworks.map((f) => {
                const adopted = adoptedIds.has(f.id);
                return (
                  <div
                    key={f.id}
                    className="flex items-start justify-between gap-4 p-4 rounded-lg border bg-card"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Globe2 className="w-4 h-4 text-muted-foreground" />
                        <span className="font-semibold">{f.name_id || f.name}</span>
                        <Badge variant="outline" className="text-xs">{f.code}</Badge>
                        {f.version && <Badge variant="secondary" className="text-xs">{f.version}</Badge>}
                      </div>
                      {f.description && (
                        <p className="text-sm text-muted-foreground mt-1">{f.description}</p>
                      )}
                    </div>
                    <div className="shrink-0">
                      {adopted ? (
                        <Badge className="bg-success/10 text-success border-success/20" variant="outline">
                          <FileCheck2 className="w-3 h-3 mr-1" /> Adopted
                        </Badge>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => adoptFramework(f.id)}
                          disabled={adoptingId === f.id || availableToAdopt.length === 0 ? false : false}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          {adoptingId === f.id ? "Adopting…" : "Adopt"}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        {/* EXPORTS */}
        <TabsContent value="exports" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileDown className="w-4 h-4" /> Audit Binder & Self-Assessment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Bangkitkan paket audit siap-cetak (cover, bukti per kontrol, gap register, chain-of-custody) atau jalankan
                pre-audit self-assessment berbasis AI untuk mendapatkan top-10 perbaikan dalam 14 hari ke depan.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground">Framework</label>
                  <Select value={exportFrameworkId} onValueChange={setExportFrameworkId}>
                    <SelectTrigger><SelectValue placeholder="Pilih framework" /></SelectTrigger>
                    <SelectContent>
                      {frameworks.map((f) => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.code} — {f.name_id || f.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-muted-foreground">Periode mulai</label>
                    <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)}
                      className="w-full h-10 rounded-md border bg-background px-3 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Periode akhir</label>
                    <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)}
                      className="w-full h-10 rounded-md border bg-background px-3 text-sm" />
                  </div>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={includeNarratives}
                  onChange={(e) => setIncludeNarratives(e.target.checked)} />
                Sertakan narasi auditor (AI)
              </label>

              <div className="flex flex-wrap gap-2 pt-2">
                <Button onClick={generateBinder} disabled={generatingBinder || !exportFrameworkId}>
                  {generatingBinder ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />}
                  {generatingBinder ? "Membangkitkan…" : "Generate Audit Binder"}
                </Button>
                <Button variant="outline" onClick={runSelfAssessment} disabled={runningSelfAssessment || !exportFrameworkId}>
                  {runningSelfAssessment ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                  {runningSelfAssessment ? "Menganalisa…" : "Pre-Audit Self-Assessment"}
                </Button>
                {binder && (
                  <Button variant="ghost" onClick={() => setBinderOpen(true)}>
                    Buka binder terakhir
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {selfAssessment && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center justify-between">
                  <span>Pre-Audit Self-Assessment</span>
                  <Badge variant="outline" className="text-base">{selfAssessment.score}%</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div className="border rounded p-2">
                    <div className="text-xs text-muted-foreground">Compliant</div>
                    <div className="font-bold text-success">{selfAssessment.summary?.compliant ?? 0}</div>
                  </div>
                  <div className="border rounded p-2">
                    <div className="text-xs text-muted-foreground">Partial</div>
                    <div className="font-bold text-warning">{selfAssessment.summary?.partial ?? 0}</div>
                  </div>
                  <div className="border rounded p-2">
                    <div className="text-xs text-muted-foreground">At Risk</div>
                    <div className="font-bold text-destructive">{selfAssessment.summary?.at_risk ?? 0}</div>
                  </div>
                  <div className="border rounded p-2">
                    <div className="text-xs text-muted-foreground">Gap</div>
                    <div className="font-bold text-muted-foreground">{selfAssessment.summary?.gap ?? 0}</div>
                  </div>
                </div>

                <div>
                  <div className="text-sm font-semibold mb-2">Top-10 perbaikan untuk 14 hari ke depan</div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-28">Klausul</TableHead>
                        <TableHead>Aksi</TableHead>
                        <TableHead className="w-28">Pemilik</TableHead>
                        <TableHead className="w-20 text-center">Hari</TableHead>
                        <TableHead className="w-24">Dampak</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(selfAssessment.fixes || []).map((fix: any, i: number) => (
                        <TableRow key={i}>
                          <TableCell className="font-mono text-xs">{fix.clause_code}</TableCell>
                          <TableCell className="text-sm">{fix.action}</TableCell>
                          <TableCell className="text-xs">{fix.owner_hint}</TableCell>
                          <TableCell className="text-center text-sm">{fix.due_in_days}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={
                              fix.impact === "high" ? "bg-destructive/10 text-destructive border-destructive/20" :
                              fix.impact === "medium" ? "bg-warning/10 text-warning border-warning/20" :
                              "bg-muted text-muted-foreground"
                            }>{fix.impact}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      <AuditBinderDialog binder={binder} open={binderOpen} onClose={() => setBinderOpen(false)} />
    </div>
  );
};

export default AdminCompliance;
